import { useEffect, useRef, useState, type ChangeEvent, type ReactNode } from 'react'

import { HEIC_ACCEPT, heicToJpeg } from '@/Ship/Helpers/heicPhoto'
import { notify } from '@/Ship/Helpers/notify'
import { PHOTO_ACCEPT, validatePhotoFile } from '@/Ship/Helpers/userPhoto'

import { PhotoCropModal } from './PhotoCropModal'

export interface PhotoCropPickerOptions {
    /**
     * Зберегти обрізаний квадрат (JPEG). Повертає true, коли вікно обрізання можна
     * закрити; помилки показує сам викликач.
     */
    onSave: (blob: Blob) => Promise<boolean>
    /** data-testid прихованого input (e2e) */
    inputTestId?: string | undefined
}

export interface PhotoCropPicker {
    /** Відкрити вибір файлу */
    pick: () => void
    /** Взяти файл (з drag-and-drop або вибору): HEIC → JPEG, перевірка → вікно обрізання */
    accept: (file: File | undefined | null) => Promise<void>
    /** Йде збереження або перетворення фото з iPhone */
    busy: boolean
    /** Прихований input і вікно обрізання — рендерити один раз поруч із тригером */
    holder: ReactNode
}

/**
 * Спільна частина вибору фото: прихований input, перевірка файлу (JPG/PNG/WEBP до
 * 15 МБ; HEIC з iPhone спершу перетворюється на JPEG), вікно обрізання в круглий кадр. Що робити з готовим квадратом — вирішує
 * викликач (профіль: PATCH користувача; картка співробітника: значення поля форми).
 */
export const usePhotoCropPicker = ({ onSave, inputTestId }: PhotoCropPickerOptions): PhotoCropPicker => {
    const inputRef = useRef<HTMLInputElement | null>(null)
    const [source, setSource] = useState<string | null>(null)
    const [busy, setBusy] = useState(false)

    // Object URL звільняємо, коли вікно закрилось або обрано інший файл
    useEffect(() => {
        if (!source) return undefined
        return () => URL.revokeObjectURL(source)
    }, [source])

    const pick = () => inputRef.current?.click()

    const accept = async (source: File | undefined | null) => {
        if (!source) return
        let file: File
        setBusy(true)
        try {
            file = await heicToJpeg(source)
        } catch (error) {
            notify.error('Не вдалося додати фото', { description: error instanceof Error ? error.message : '' })
            return
        } finally {
            setBusy(false)
        }
        const problem = validatePhotoFile(file)
        if (problem) {
            notify.error('Не вдалося додати фото', { description: problem })
            return
        }
        setSource(URL.createObjectURL(file))
    }

    const onInputChange = (event: ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0]
        // Скидаємо, щоб повторний вибір того самого файлу теж спрацював
        event.target.value = ''
        void accept(file)
    }

    const save = async (blob: Blob) => {
        setBusy(true)
        try {
            if (await onSave(blob)) setSource(null)
        } finally {
            setBusy(false)
        }
    }

    const holder = (
        <>
            <input
                ref={inputRef}
                type="file"
                accept={[...PHOTO_ACCEPT, HEIC_ACCEPT].join(',')}
                hidden
                onChange={onInputChange}
                data-testid={inputTestId}
            />
            <PhotoCropModal
                src={source}
                busy={busy}
                onCancel={() => !busy && setSource(null)}
                onSave={(blob) => void save(blob)}
            />
        </>
    )

    return { pick, accept, busy, holder }
}
